/**
 * Guardia "il saldo non va sotto zero" — parsing del 422 strutturato.
 *
 * Il backend rifiuta ogni movimento che porterebbe il saldo di (area, materiale)
 * sotto zero e risponde con un detail OGGETTO che porta i numeri: disponibile,
 * richiesto, mancante. Servono a video — «non ci sono abbastanza kg» senza dire
 * quanti ce ne sono è inutile a chi deve decidere cosa fare — e serviranno alla
 * schermata di allocazione del residuo nella shell OP.
 *
 * Attenzione al motivo per cui questo parser esiste: un detail oggetto passato
 * a un `setError(detail ?? …)` finisce in JSX e fa esplodere il componente
 * (React #31). `apiErrorMessage` non lo copre — legge solo detail stringa o
 * array Pydantic — e non va esteso per un caso singolo.
 *
 * Stessa disciplina di modificaFiscale.ts e cerLedger.ts: parser puro,
 * discriminante `code`, null per qualunque altro errore.
 */

export interface SaldoInsufficienteDetail {
  /** `saldo_gia_negativo`: dato storico da correggere, non un errore dell'utente. */
  code: 'saldo_insufficiente' | 'saldo_gia_negativo';
  area_id: string;
  area_codice: string | null;
  materiale: string;
  disponibile_kg: number;
  richiesto_kg: number;
  mancante_kg: number;
}

function numero(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function parseSaldoInsufficiente(err: unknown): SaldoInsufficienteDetail | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 422) return null;
  const detail = response.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Partial<SaldoInsufficienteDetail>;
  if (d.code !== 'saldo_insufficiente' && d.code !== 'saldo_gia_negativo') return null;
  return {
    code: d.code,
    area_id: String(d.area_id ?? ''),
    area_codice: d.area_codice ?? null,
    materiale: String(d.materiale ?? ''),
    disponibile_kg: numero(d.disponibile_kg),
    richiesto_kg: numero(d.richiesto_kg),
    mancante_kg: numero(d.mancante_kg),
  };
}

/** kg in formato italiano, senza decimali inutili.
 *
 * Due trappole, entrambe viste in produzione sulla pagina diagnostica:
 *
 *  1. i pesi NON arrivano sempre come number. I campi `Decimal` di Pydantic
 *     sono serializzati in JSON come STRINGA, e asyncpg restituisce i NUMERIC
 *     interi in forma esponenziale: `Decimal('2E+4')` diventa `"2E+4"`. Su una
 *     stringa `toLocaleString` e' quello di String.prototype, che restituisce
 *     la stringa tal quale: il formatter c'era ed era un no-op silenzioso.
 *     Da qui la coercizione — `Number('2E+4')` fa 20000.
 *  2. l'italiano NON raggruppa le migliaia a quattro cifre: CLDR it-IT ha
 *     `minimumGroupingDigits: 2`, quindi 8650 resta "8650" mentre 20000
 *     diventa "20.000". Nella stessa frase i due numeri sembravano scritti
 *     con due regole diverse. `useGrouping: 'always'` lo impedisce.
 *
 * `decimaliMax` esiste per UN caso e va tenuto raro: le KPI della Dashboard,
 * dove i centesimi di un totale di magazzino sono rumore. Non e' una
 * preferenza estetica per-pagina: due decimali sono il default perche' i pesi
 * a ledger ne hanno due, e chi lo abbassa sta dicendo che quel numero non e'
 * un peso da confrontare con il registro ma una sintesi.
 */
export function fmtKg(kg: number | string, decimaliMax = 2): string {
  const n = typeof kg === 'number' ? kg : Number(kg);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('it-IT', { maximumFractionDigits: decimaliMax, useGrouping: 'always' });
}

/** Percentuale in formato italiano: separatore decimale VIRGOLA, non punto.
 *  Stessa coercizione di `fmtKg` — `scarto_pct` e' un Decimal sul ledger. */
export function fmtPct(v: number | string): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('it-IT', { maximumFractionDigits: 1 });
}
